import { useCallback, useEffect, useState } from "react";
import apiRequest from "../../../utils/apiClient";
import { autoDestinationUrl, autoDestinationTestUrl } from "../../constants";
import { AUTO_TAB } from "../constants/accountText";

/** The saved webhook, as the backend reports it (transcript/models.py AutoDestination). */
export type PostWhen = "now" | "review";
export type Privacy = "private" | "public";

export interface AutoDestination {
  url: string;
  enabled: boolean;
  /** now = send each finished video; review = wait for the Post button. */
  post_when?: PostWhen;
  privacy?: Privacy;
  last_fired_at: string | null;
  last_status: number | null;
  last_error: string;
}

/** What auto-destination/test/ answers with. */
interface TestResponse {
  ok?: boolean;
  status?: number | null;
  message?: string;
  destination?: AutoDestination | null;
}

interface UseAutoDestinationResult {
  destination: AutoDestination | null;
  loading: boolean;
  saving: boolean;
  testing: boolean;
  /** A problem the user has to fix (bad URL, webhook refused the packet). */
  error: string;
  /** Something went right — cleared as soon as they touch anything. */
  notice: string;
  save: (url: string, enabled: boolean, postWhen?: PostWhen, privacy?: Privacy) => Promise<void>;
  disconnect: () => Promise<void>;
  sendTest: () => Promise<void>;
  clearMessages: () => void;
}

const messageFrom = async (response: Response, fallback: string): Promise<string> => {
  try {
    const data = await response.json();
    return data?.message || fallback;
  } catch {
    return fallback;
  }
};

export const useAutoDestination = (): UseAutoDestinationResult => {
  const [destination, setDestination] = useState<AutoDestination | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiRequest(autoDestinationUrl, { method: "GET" })
      .then(async (response) => {
        if (cancelled) return;
        if (!response.ok) {
          setError(AUTO_TAB.LOADING_ERROR);
          return;
        }
        const data = await response.json();
        setDestination(data?.destination ?? null);
      })
      .catch(() => {
        if (!cancelled) setError(AUTO_TAB.LOADING_ERROR);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const clearMessages = useCallback(() => {
    setError("");
    setNotice("");
  }, []);

  const post = useCallback(
    async (url: string, enabled: boolean, successNotice: string, mode: { post_when?: PostWhen; privacy?: Privacy } = {}) => {
      setSaving(true);
      clearMessages();
      try {
        const response = await apiRequest(autoDestinationUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url, enabled, ...mode }),
        });
        if (!response.ok) {
          setError(await messageFrom(response, AUTO_TAB.SAVE_ERROR));
          return;
        }
        const data = await response.json();
        setDestination(data?.destination ?? null);
        setNotice(successNotice);
      } catch {
        setError(AUTO_TAB.SAVE_ERROR);
      } finally {
        setSaving(false);
      }
    },
    [clearMessages],
  );

  const save = useCallback(
    (url: string, enabled: boolean, postWhen?: PostWhen, privacy?: Privacy) =>
      post(url, enabled, AUTO_TAB.SAVED, {
        ...(postWhen ? { post_when: postWhen } : {}),
        ...(privacy ? { privacy } : {}),
      }),
    [post],
  );

  // An empty URL is how the backend deletes the row — disconnecting is a save of "".
  const disconnect = useCallback(() => post("", false, ""), [post]);

  const sendTest = useCallback(async (): Promise<void> => {
    setTesting(true);
    clearMessages();
    try {
      const response = await apiRequest(autoDestinationTestUrl, { method: "POST" });
      let data: TestResponse | null = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      if (data?.destination) setDestination(data.destination);
      if (response.ok && data?.ok) {
        setNotice(AUTO_TAB.TEST_SENT);
      } else {
        setError(data?.message || AUTO_TAB.SAVE_ERROR);
      }
    } catch {
      setError(AUTO_TAB.SAVE_ERROR);
    } finally {
      setTesting(false);
    }
  }, [clearMessages]);

  return {
    destination,
    loading,
    saving,
    testing,
    error,
    notice,
    save,
    disconnect,
    sendTest,
    clearMessages,
  };
};
